import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RENAISSANCE_COLORS } from '../utils';
import { CSSProperties } from 'react';
import "./Home.css";
import Header from '../components/Header';
import apiService, { RandomRecord, ScoringResult } from '../services/api';
import { toast } from 'react-toastify';
import { Box, Container, Paper, Typography, Button, CircularProgress } from '@mui/material';

function Home() {
  const location = useLocation();
  const [isScoring, setIsScoring] = useState(false);
  const [scoringResult, setScoringResult] = useState<ScoringResult | null>(null);
  const responseData = location.state as RandomRecord;

  const handleScore = async () => {
    if (!responseData) {
      toast.error('No response data available');
      return;
    }

    try {
      setIsScoring(true);
      const result = await apiService.scoreResponse(responseData);
      setScoringResult(result);
      toast.success('Response scored successfully!');
    } catch (error) {
      toast.error('Failed to score response. Please try again.');
      console.error('Scoring error:', error);
    } finally {
      setIsScoring(false);
    }
  };

  if (!responseData) {
    return (
      <div className="Home" style={{
        backgroundColor: RENAISSANCE_COLORS.beige,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}>
        <Header />
        <Container maxWidth="lg">
          <Box sx={{ mt: 6, mb: 6 }}>
            {/* Hero Section */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: RENAISSANCE_COLORS.black,
                  mb: 2
                }}
              >
                AI Scoring Evaluation Service
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 400,
                  maxWidth: 700,
                  mx: 'auto'
                }}
              >
                Automated scoring system for student constructed responses and writing prompts powered by AWS Bedrock Claude models
              </Typography>
            </Box>

            {/* Feature Cards Grid */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 3,
              mb: 4
            }}>
              {/* WP Evaluation Card */}
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: RENAISSANCE_COLORS.black }}>
                  📝 WP Evaluation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  Run Writing Prompt evaluations with multi-trait scoring and detailed explanations per trait
                </Typography>
              </Paper>

              {/* CR Evaluation Card */}
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: RENAISSANCE_COLORS.black }}>
                  ✍️ CR Evaluation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  Run Constructed Response evaluations with single holistic scores bounded to rubric scale
                </Typography>
              </Paper>

              {/* Evaluations Card */}
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: RENAISSANCE_COLORS.black }}>
                  📊 Evaluations
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  View evaluation jobs, monitor their status, and track progress of ongoing scoring operations
                </Typography>
              </Paper>

              {/* Results Card */}
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: RENAISSANCE_COLORS.black }}>
                  📈 Results
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  Browse evaluation results, analyze scoring metrics, and review QWK performance data
                </Typography>
              </Paper>

              {/* Datasets Card */}
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: RENAISSANCE_COLORS.black }}>
                  🗂️ Datasets
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  Manage datasets stored in S3, upload new parquet or CSV files for evaluation
                </Typography>
              </Paper>

              {/* API Docs Card */}
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: RENAISSANCE_COLORS.black }}>
                  📖 API Docs
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  View interactive API documentation with Swagger UI for all scoring endpoints
                </Typography>
              </Paper>
            </Box>

            {/* Quick Stats Section */}
            <Paper sx={{ p: 4, mt: 4, bgcolor: RENAISSANCE_COLORS.white }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
                Key Features
              </Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 3,
                textAlign: 'center'
              }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: RENAISSANCE_COLORS.black, mb: 1 }}>
                    🤖
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    AI-Powered
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    AWS Bedrock Claude models
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: RENAISSANCE_COLORS.black, mb: 1 }}>
                    ⚡
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Batch Scoring
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Time & size-based buffering
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: RENAISSANCE_COLORS.black, mb: 1 }}>
                    💾
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Score Cache
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cross-container deduplication
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Container>
        <footer style={{
          backgroundColor: RENAISSANCE_COLORS.white,
          padding: '12px 0',
          borderTop: `1px solid ${RENAISSANCE_COLORS.lightgrey}`,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.05)',
          marginTop: 'auto'
        }}>
          <div style={styles.footerContent}>
            <div style={styles.footerMetadata}>
              <div style={styles.footerText}>
                Autoscoringevalsvc v0.1
              </div>
            </div>
            <img
              src="ren-cropped.svg"
              alt="Renaissance Logo"
              style={styles.footerLogo}
            />
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="Home" style={{
      backgroundColor: RENAISSANCE_COLORS.beige,
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
    }}>
      <Header />
      <Box sx={{ p: 3, flex: 1 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>Response Details</Typography>

          {/* Grade Level */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Grade Level
            </Typography>
            <Typography variant="body1">
              {responseData.gradeLevel}
            </Typography>
          </Box>

          {/* Reading Passage */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Reading Passage
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <div
                dangerouslySetInnerHTML={{ __html: responseData.readingPassage }}
                className="reading-passage"
              />
            </Paper>
          </Box>

          {/* Question Prompt */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Question Prompt
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <div
                dangerouslySetInnerHTML={{ __html: responseData.questionPrompt }}
              />
            </Paper>
          </Box>

          {/* Student Response */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Student Response
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <div
                dangerouslySetInnerHTML={{ __html: responseData.studentResponse }}
              />
            </Paper>
          </Box>

          {/* Scoring Result */}
          {scoringResult && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Scoring Result</Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Total Score:</strong> {scoringResult.score}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Confidence:</strong> {(scoringResult.confidence * 100).toFixed(1)}%
                </Typography>

                {/* Display individual traits */}
                {scoringResult.traits && scoringResult.traits.map((trait, index) => (
                  <Box key={index} sx={{ mt: 2, pb: 2, borderBottom: index < scoringResult.traits.length - 1 ? '1px solid #eee' : 'none' }}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      {trait.trait_name}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Trait Score:</strong> {trait.trait_score}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Trait Explanation:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ pl: 2, mb: 1 }}>
                      {trait.trait_explanation}
                    </Typography>
                  </Box>
                ))}

                {/* Overall explanation */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1">
                    <strong>Overall Feedback:</strong>
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {scoringResult.explanation}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleScore}
              disabled={isScoring}
              startIcon={isScoring ? <CircularProgress size={20} /> : null}
            >
              {isScoring ? 'Scoring...' : 'Score Response'}
            </Button>
          </Box>
        </Paper>
      </Box>

      <footer style={{
        backgroundColor: RENAISSANCE_COLORS.white,
        padding: '12px 0',
        borderTop: `1px solid ${RENAISSANCE_COLORS.lightgrey}`,
        boxShadow: '0 -2px 10px rgba(0,0,0,0.05)'
      }}>
        <div style={styles.footerContent}>
          <div style={styles.footerMetadata}>
            <div style={styles.footerText}>
              Autoscoringevalsvc v0.1
            </div>
          </div>

          <img
            src="ren-cropped.svg"
            alt="Renaissance Logo"
            style={styles.footerLogo}
          />
        </div>
      </footer>
    </div>
  );
}

const styles: { [key: string]: CSSProperties } = {
  footerContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 20px'
  },
  footerMetadata: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  footerText: {
    fontSize: '12px',
    marginBottom: '3px'
  },
  footerLogo: {
    height: '30px'
  }
};

export default Home;